'use client';

import type { ReactNode } from 'react';
import { useRef, useState } from 'react';

/* Inhaltsformen gespiegelt aus ../../_content.tsx, damit dieses Client-Modul
   nie den Server-Inhaltsbaum importiert. */
export interface LifecycleTab {
  trigger: string;
  heading: string;
  body: ReactNode;
  keepBody: ReactNode;
  statValue: string;
  statLabel: string;
}

function ClockIcon() {
  return (
    <svg viewBox="0 0 24 24">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}

function RefreshIcon() {
  return (
    <svg viewBox="0 0 24 24">
      <path d="M21 12a9 9 0 1 1-9-9" />
      <polyline points="21 3 21 9 15 9" />
    </svg>
  );
}

function CalendarIcon() {
  return (
    <svg viewBox="0 0 24 24">
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function DocIcon() {
  return (
    <svg viewBox="0 0 24 24">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
    </svg>
  );
}

function KeyIcon() {
  return (
    <svg viewBox="0 0 24 24">
      <circle cx="7.5" cy="15.5" r="4.5" />
      <path d="M10.7 12.3 21 2" />
      <path d="m16 7 3 3" />
      <path d="m19 4 2 2" />
    </svg>
  );
}

function MessageIcon() {
  return (
    <svg viewBox="0 0 24 24">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  );
}

/** Symbole der Kennkarten, in Reiterfolge. */
const STAT_ICONS = [
  <ClockIcon key="0" />,
  <RefreshIcon key="1" />,
  <CalendarIcon key="2" />,
  <MessageIcon key="3" />,
  <DocIcon key="4" />,
  <CheckIcon key="5" />,
  <KeyIcon key="6" />,
];

function TabStatCard({
  icon,
  value,
  label,
}: {
  icon: ReactNode;
  value: string;
  label: string;
}) {
  return (
    <div className="tab-statcard">
      <span className="tab-statcard-icon">{icon}</span>
      <span className="tab-statcard-text">
        <span className="tab-statcard-value">{value}</span>
        <span className="tab-statcard-label">{label}</span>
      </span>
    </div>
  );
}

function KeepBlock({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="tab-keepblock">
      <span className="tab-keepblock-label">{label}</span>
      <p>{children}</p>
    </div>
  );
}

/**
 * „So funktioniert’s“: die sieben Schritte einer Vermietung als Reiter. Je
 * Reiter erklärt der Text, was Locari macht, „Was bei dir bleibt“ sagt, was
 * beim Vermieter bleibt, die Kennkarte fasst es zusammen. Das Produktvideo
 * (#1760) begleitet den Text rechts daneben (`bilder`, von der Seite gesetzt;
 * am Telefon unter dem Text). Nur der gewählte Reiter ist sichtbar, also
 * läuft auch nur sein Video (`ProduktVideo` startet erst im Bild).
 *
 * Wieder eingeführt am 31.10.2026 (Auftraggeber: „nur noch Videos
 * untereinander“); die Fassung `SchrittFolge` mit sechs Videos untereinander
 * ist gelöscht.
 *
 * Schwester gleichen Musters, bewusst getrennt (andere Datenform, andere
 * ARIA-Struktur): `_components/fuer-grossvermieter/lifecycle-tabs.tsx`.
 */
export function LifecycleTabsService({
  regionLabel,
  keepLabel,
  freenote,
  tabs,
  bilder,
}: {
  regionLabel: string;
  keepLabel: string;
  freenote: string;
  tabs: LifecycleTab[];
  /** Video je Reiter, `null` = keins (ohne Videoquelle). */
  bilder: (ReactNode | null)[];
}) {
  const [active, setActive] = useState(0);
  const triggerRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const onKey = (e: React.KeyboardEvent, i: number) => {
    let next: number | null = null;

    if (e.key === 'ArrowRight') next = (i + 1) % tabs.length;
    else if (e.key === 'ArrowLeft') next = (i - 1 + tabs.length) % tabs.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = tabs.length - 1;

    if (next !== null) {
      e.preventDefault();
      setActive(next);
      triggerRefs.current[next]?.focus();
    }
  };

  return (
    <div className="tabs reveal" role="region" aria-label={regionLabel}>
      <div className="tabs-bar" role="tablist">
        {tabs.map((tab, i) => (
          <button
            key={tab.trigger}
            ref={(el) => {
              triggerRefs.current[i] = el;
            }}
            className="tab-trigger"
            role="tab"
            id={`tab-trigger-${i + 1}`}
            aria-selected={i === active}
            aria-controls={`tab-panel-${i + 1}`}
            data-tab={i + 1}
            data-test={`schritt-reiter-${i + 1}`}
            tabIndex={i === active ? 0 : -1}
            onClick={() => setActive(i)}
            onKeyDown={(e) => onKey(e, i)}
          >
            <span className="tab-trigger-num">{i + 1}</span> {tab.trigger}
          </button>
        ))}
      </div>

      {tabs.map((tab, i) => {
        const bild = bilder[i] ?? null;

        return (
          <div
            key={tab.trigger}
            className={i === active ? 'tab-panel is-active' : 'tab-panel'}
            role="tabpanel"
            id={`tab-panel-${i + 1}`}
            aria-labelledby={`tab-trigger-${i + 1}`}
            data-active={i === active}
            data-test={`schritt-${i + 1}`}
            hidden={i !== active}
          >
            <div className="tab-panel-body">
              <div className="tab-copy">
                <h3>{tab.heading}</h3>
                <p>{tab.body}</p>
                <KeepBlock label={keepLabel}>{tab.keepBody}</KeepBlock>
                {i === 0 ? (
                  <span className="tab-freenote">
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                    {freenote}
                  </span>
                ) : null}
              </div>
              <div className="tab-visual">
                {bild}
                <TabStatCard
                  icon={STAT_ICONS[i]}
                  value={tab.statValue}
                  label={tab.statLabel}
                />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
